import { checkChatResponse } from './apiErrors'
import { ModelSelection, ThreadResponse } from './features/model-testing/types'

export type QuestionnaireResponse = {
  filledQuestionnaire: string // JSON string of filled answers
}

export type QuestionnaireSubmitResponse = {
  success: boolean
  message?: string
}

// Add these new functions
export const fillQuestionnaire = async (
  bearerToken: string,
  chatServer: string,
  text: string,
): Promise<QuestionnaireResponse> => {
  const response = await fetch(`${chatServer}/questionnaire/autofill`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${bearerToken}`,
    },
    body: JSON.stringify({ text }),
  })
  if (!response.ok) {
    throw new Error('Failed to fill questionnaire')
  }
  return response.json()
}

export const submitQuestionnaire = async (
  bearerToken: string,
  chatServer: string,
  answers: Record<string, any>,
): Promise<QuestionnaireSubmitResponse> => {
  const response = await fetch(`${chatServer}/questionnaire/answers`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${bearerToken}`,
    },
    body: JSON.stringify({ answers }),
  })
  if (!response.ok) {
    throw new Error('Failed to submit questionnaire')
  }
  return response.json()
}

export const getQuestionnaireFields = async (
  bearerToken: string,
  chatServer: string,
): Promise<any[]> => {
  const response = await fetch(`${chatServer}/questionnaire/questions`, {
    headers: {
      Authorization: `Bearer ${bearerToken}`,
    },
  })
  if (!response.ok) {
    throw new Error('Failed to get questionnaire fields')
  }
  const json = await response.json()
  return json.questions
}

export const createNewChatbotThread = async (
  bearerToken: string,
  chatServer: string,
  modelAlias?: string | null,
): Promise<ThreadResponse> => {
  const response = await fetch(chatServer + '/threads', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + bearerToken,
      ...(modelAlias !== undefined ? { 'X-Model-Testing': 'true' } : {}),
    },
    body: modelAlias === undefined ? undefined : JSON.stringify({ modelAlias }),
  })
  await checkChatResponse(response)
  const thread: ThreadResponse = await response.json()
  if (!thread.id) throw new Error('Missing thread id')
  return thread
}

export const getChatbotThreadModel = async (
  bearerToken: string,
  chatServer: string,
  threadId: string,
): Promise<ModelSelection> => {
  const response = await fetch(
    `${chatServer}/threads/${encodeURIComponent(threadId)}/model`,
    { headers: { Authorization: 'Bearer ' + bearerToken } },
  )
  await checkChatResponse(response)
  const body: { modelSelection: ModelSelection } = await response.json()
  if (!body.modelSelection) throw new Error('Missing model selection')
  return body.modelSelection
}

export const postChatMsgToThreadAndStreamResponse = async function* (
  bearerToken: string,
  chatServer: string,
  threadId: string,
  messageText: string,
  systemPromptId: string | null, // If NULL, then custom.
  customSystemPrompt: string | null,
) {
  const newChatResponse = await fetch(
    chatServer + '/threads/' + threadId + '/messages',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + bearerToken,
      },
      body: JSON.stringify({
        message: messageText,
        systemPromptId: systemPromptId,
        customSystemPrompt:
          systemPromptId == null ? customSystemPrompt : undefined,
      }),
    },
  )

  await checkChatResponse(newChatResponse)
  const reader = newChatResponse.body?.getReader()
  if (!reader) throw new Error('Missing response stream')
  const decoder = new TextDecoder()
  let buffer = ''
  try {
    while (true) {
      const result = await reader.read()
      buffer += decoder.decode(result.value, { stream: !result.done })
      const lines = buffer.split('\n')
      buffer = lines.pop() || ''
      for (const line of lines) {
        if (line.trim()) yield JSON.parse(line)
      }
      if (result.done) break
    }
    if (buffer.trim()) yield JSON.parse(buffer)
  } finally {
    await reader.cancel().catch(() => {})
    reader.releaseLock()
  }
}

export type AlternativeSystemPrompt = {
  name: string
  prompt?: string
}

export type Me = {
  systemPrompt: string | null
  isAdmin: boolean
  canUseCustomSystemPrompt: boolean
  alternativeSystemPrompts: AlternativeSystemPrompt[]
}
export const getMe = async (
  token: string,
  chatServer: string,
): Promise<Me | null> => {
  const response = await fetch(chatServer + '/me', {
    headers: {
      Authorization: 'Bearer ' + token,
    },
  })
  if (response.ok) {
    return await response.json()
  } else {
    console.error('Failed to GET /me')
    return null
  }
}
